import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    PackageSearch,
    ClipboardList,
    LogOut,
    Store,
    Menu,
    X,
    ShoppingCart,
    Mail,
    MessageSquareText,
    Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { LOGO_URL } from "@/constants/brand";

const NAV = [
    { to: "/admin", icon: LayoutDashboard, label: "Analytics", end: true },
    { to: "/admin/orders", icon: ClipboardList, label: "Orders" },
    { to: "/admin/products", icon: PackageSearch, label: "Products" },
    { to: "/admin/abandoned", icon: ShoppingCart, label: "Abandoned" },
    { to: "/admin/subscribers", icon: Mail, label: "Subscribers" },
    { to: "/admin/reviews", icon: MessageSquareText, label: "Reviews" },
    { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
];

export default function AdminLayout() {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/admin/login", { replace: true });
    };

    return (
        <div
            data-testid="admin-layout"
            className="min-h-screen bg-cream flex font-sans text-forest-900"
        >
            {/* Sidebar */}
            <aside
                className={`fixed lg:static z-40 h-full lg:h-auto lg:min-h-screen w-72 bg-forest-900 text-cream p-6 flex flex-col transition-transform ${
                    open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
                }`}
            >
                <Link to="/admin" className="flex items-center gap-3 mb-10">
                    <img
                        src={LOGO_URL}
                        alt="Resham Drops"
                        className="w-11 h-11 rounded-full border-2 border-gold-500 object-cover"
                    />
                    <div>
                        <div className="font-serif font-bold text-cream text-lg leading-tight">
                            Resham Drops
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-gold-400">
                            Admin Console
                        </div>
                    </div>
                </Link>

                <nav className="flex-1 space-y-1">
                    {NAV.map(({ to, icon: Icon, label, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            onClick={() => setOpen(false)}
                            data-testid={`admin-nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                                    isActive
                                        ? "bg-gold-500/15 text-gold-400 border border-gold-500/30"
                                        : "text-cream/80 hover:bg-forest-800 hover:text-gold-400"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="space-y-3 pt-6 border-t border-cream/10">
                    <Link
                        to="/"
                        className="flex items-center gap-2 text-xs text-cream/70 hover:text-gold-400"
                    >
                        <Store className="w-4 h-4" /> View store
                    </Link>
                    <div className="text-[11px] text-cream/50">
                        Signed in as{" "}
                        <span className="text-gold-400 font-semibold">
                            {user?.email}
                        </span>
                    </div>
                    <button
                        onClick={handleLogout}
                        data-testid="admin-logout"
                        className="w-full flex items-center justify-center gap-2 bg-forest-800 hover:bg-forest-700 text-gold-400 text-xs font-bold py-2.5 rounded-xl border border-gold-500/30"
                    >
                        <LogOut className="w-4 h-4" />
                        Logout
                    </button>
                </div>
            </aside>

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 bg-forest-900/50 z-30 lg:hidden"
                />
            )}

            <main className="flex-1 lg:ml-0 min-h-screen">
                <div className="lg:hidden sticky top-0 z-20 bg-cream border-b border-forest-100 flex items-center justify-between px-4 py-3">
                    <button
                        onClick={() => setOpen(true)}
                        className="w-10 h-10 rounded-xl bg-forest-800 text-gold-400 flex items-center justify-center"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                    <span className="font-serif font-bold text-forest-800">
                        Resham Drops Admin
                    </span>
                    <span className="w-10" />
                </div>
                <div className="p-5 sm:p-8 lg:p-10">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
